// utils/enumUtils.ts

/**
 * 通用枚举映射器返回类型
 */
export type EnumMapper<T extends number | string> = {
  /**
   * 根据枚举值获取标签
   */
  getLabel: (value: T) => string

  /**
   * 生成 Select 选项
   */
  toOptions: (config?: {
    includeAll?: boolean
    allLabel?: string
    allValue?: T
    sort?: 'label' | 'value' | 'none'
  }) => { label: string; value: T }[]
}

/**
 * 创建一个通用的枚举映射器
 * @param enumObj 枚举对象
 * @param labelMap 描述映射对象
 * @returns EnumMapper
 */
export const createEnumMapper = <T extends number | string>(
  enumObj: Record<string, unknown>,
  labelMap: Record<T, string>
): EnumMapper<T> => {
  // 提取所有合法的枚举值（数字或字符串）
  const isValidValue = (v: unknown): v is T =>
    (typeof v === 'string' || typeof v === 'number') && v in labelMap

  const values = Object.values(enumObj).filter(isValidValue)

  return {
    getLabel: (value: T): string => {
      return labelMap[value]
    },

    toOptions: ({
      includeAll = false,
      allLabel = '不限',
      allValue = 0 as T,
      sort = 'none'
    } = {}) => {
      const options = values.map((value) => ({
        label: labelMap[value],
        value
      }))

      // 排序
      if (sort === 'label') {
        options.sort((a, b) => a.label.localeCompare(b.label))
      } else if (sort === 'value') {
        options.sort((a, b) => (a.value as number) - (b.value as number))
      }

      // 添加“不限”
      if (includeAll) {
        options.unshift({ label: allLabel, value: allValue })
      }

      return options
    }
  }
}
